import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Siren, Loader2, Activity, Slack } from 'lucide-react';
import { format } from 'date-fns';

const WatchdogCard = ({
  watchdog, setWatchdog,
  runningWatchdog, runWatchdogNow,
  testingSlack, testSlack,
  savingWatchdog, saveWatchdog,
}) => {
  if (!watchdog) return null;
  return (
    <Card className="border-[#E5E7EB]" data-testid="watchdog-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Siren className="w-4 h-4 text-[#FF3B30]" />Readiness Watchdog (auto-regression alert)
            {watchdog.enabled ? (
              <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" data-testid="watchdog-armed-badge">Armed</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-[#9CA3AF]" data-testid="watchdog-disabled-badge">Disabled</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="outline" onClick={runWatchdogNow} disabled={runningWatchdog || !watchdog.recipient_email}
              data-testid="watchdog-run-now-btn"
            >
              {runningWatchdog ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Activity className="w-3 h-3 mr-1" />}
              Run Now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-[11px] text-[#4B5563] mb-3">
          Runs the full 4-pathway readiness check on a schedule and emails the recipient when pass rate drops below the configured threshold. Skip the manual weekly audit — only get pinged when something breaks.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <Label className="text-[10px] text-[#4B5563]">Day of week</Label>
            <select
              className="w-full mt-1 border border-[#E5E7EB] rounded px-2 py-1.5 text-sm"
              value={watchdog.day_of_week || 'mon'}
              onChange={e => setWatchdog({ ...watchdog, day_of_week: e.target.value })}
              data-testid="watchdog-day-select"
            >
              {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                <option key={d} value={d}>{d.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Hour (UTC)</Label>
            <select
              className="w-full mt-1 border border-[#E5E7EB] rounded px-2 py-1.5 text-sm"
              value={watchdog.hour ?? 7}
              onChange={e => setWatchdog({ ...watchdog, hour: Number(e.target.value) })}
              data-testid="watchdog-hour-select"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Alert recipient</Label>
            <Input
              value={watchdog.recipient_email || ''}
              onChange={e => setWatchdog({ ...watchdog, recipient_email: e.target.value })}
              placeholder="ops@yourdomain.com"
              className="mt-1"
              data-testid="watchdog-recipient-input"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Alert threshold (%)</Label>
            <Input
              type="number" min={0} max={100}
              value={watchdog.alert_threshold_pct ?? 100}
              onChange={e => setWatchdog({ ...watchdog, alert_threshold_pct: Number(e.target.value) })}
              className="mt-1"
              data-testid="watchdog-threshold-input"
            />
          </div>
        </div>
        <div className="mb-3" data-testid="watchdog-slack-row">
          <Label className="text-[10px] text-[#4B5563] flex items-center gap-1">
            <Slack className="w-3 h-3" />Slack webhook URL <span className="text-[#9CA3AF]">(fallback channel — survives email outages)</span>
          </Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={watchdog.slack_webhook_url || ''}
              onChange={e => setWatchdog({ ...watchdog, slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/T000/B000/xxxxxxxx"
              className="flex-1 font-mono text-xs"
              data-testid="watchdog-slack-input"
            />
            <Button
              size="sm" variant="outline" onClick={testSlack}
              disabled={testingSlack || !(watchdog.slack_webhook_url || '').trim()}
              data-testid="watchdog-test-slack-btn"
            >
              {testingSlack ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Slack className="w-3 h-3 mr-1" />}
              Test
            </Button>
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-1">
            Create one at <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="underline">api.slack.com/messaging/webhooks</a> — paste the URL (it includes the secret).
          </p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-[#F3F4F6]">
          <label className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!watchdog.enabled}
              onChange={e => setWatchdog({ ...watchdog, enabled: e.target.checked })}
              data-testid="watchdog-enabled-toggle"
            />
            Arm watchdog (run weekly)
          </label>
          <Button
            size="sm" onClick={saveWatchdog} disabled={savingWatchdog}
            className="bg-[#002FA7] hover:bg-[#00227A]"
            data-testid="watchdog-save-btn"
          >
            {savingWatchdog ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
          </Button>
        </div>
        {watchdog.last_run_at && (
          <div className="mt-3 p-2 bg-[#FAFAFA] rounded text-[11px] text-[#4B5563]" data-testid="watchdog-last-run">
            Last run <strong>{format(new Date(watchdog.last_run_at), 'MMM d, HH:mm')}</strong> UTC —{' '}
            <strong className="text-[#0A0A0A]">{watchdog.last_run_passed}/{watchdog.last_run_total}</strong> passed
            {watchdog.last_alert_sent_at && watchdog.last_alert_sent_at === watchdog.last_run_at && (
              <span className="text-[#FF3B30]"> · alert dispatched
                {watchdog.last_alerted_email && ' (email)'}
                {watchdog.last_alerted_slack && watchdog.last_alerted_email && ' +'}
                {watchdog.last_alerted_slack && ' (slack)'}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WatchdogCard;
